import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from "react";
import { adminUrl } from '@/lib/siteUrls';

interface AdminUser {
  id: number;
  name: string;
  email: string;
  role: "super-admin" | "admin" | "editor";
  personal_email?: string;
  gmail_email?: string;
}

interface AdminAuthContextType {
  user: AdminUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  hasRole: (roles: string[]) => boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
}

const AdminAuthContext = createContext<AdminAuthContextType | null>(null);
const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000;

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const inactivityTimerRef = useRef<number | null>(null);

  // Validate token on mount
  useEffect(() => {
    const validate = async () => {
      const storedToken = localStorage.getItem("adminToken");
      if (!storedToken) {
        setIsLoading(false);
        return;
      }

      try {
        const res = await fetch("/api/admin/auth", {
          headers: { Authorization: `Bearer ${storedToken}` },
        });
        const data = await res.json();

        if (res.ok && data.user) {
          setUser(data.user);
        } else {
          localStorage.removeItem("adminToken");
          localStorage.removeItem("adminUser");
        }
      } catch (err) {
        console.error("Token validation error:", err);
        localStorage.removeItem("adminToken");
        localStorage.removeItem("adminUser");
      } finally {
        setIsLoading(false);
      }
    };

    validate();
  }, []);

  const hasRole = useCallback(
    (roles: string[]) => {
      if (!user) return false;
      return roles.includes(user.role);
    },
    [user]
  );

  const login = useCallback(
    async (email: string, password: string) => {
      try {
        const res = await fetch("/api/admin/auth", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, password }),
        });
        const data = await res.json();

        if (res.ok && data.success) {
          localStorage.setItem("adminToken", data.token);
          localStorage.setItem("adminUser", JSON.stringify(data.user));
          setUser(data.user);
          return { success: true };
        }
        return { success: false, error: data.error || "Login failed" };
      } catch (err) {
        console.error("Login error:", err);
        return { success: false, error: "Network error. Please try again." };
      }
    },
    []
  );

  const logout = useCallback(async () => {
    const storedToken = localStorage.getItem("adminToken");
    if (storedToken) {
      try {
        await fetch("/api/admin/auth", {
          method: "DELETE",
          headers: { Authorization: `Bearer ${storedToken}` },
        });
      } catch (err) {
        console.error("Logout API error:", err);
      }
    }
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminUser");
    setUser(null);
    window.location.href = adminUrl('/login');
  }, []);

  useEffect(() => {
    if (!user) {
      if (inactivityTimerRef.current) {
        window.clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
      return;
    }

    const resetInactivityTimer = () => {
      if (inactivityTimerRef.current) {
        window.clearTimeout(inactivityTimerRef.current);
      }
      inactivityTimerRef.current = window.setTimeout(() => {
        logout();
      }, INACTIVITY_TIMEOUT_MS);
    };

    const activityEvents = ["mousedown", "mousemove", "keydown", "scroll", "touchstart", "click", "focus"];
    activityEvents.forEach((event) => window.addEventListener(event, resetInactivityTimer, { passive: true }));
    resetInactivityTimer();

    return () => {
      if (inactivityTimerRef.current) {
        window.clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
      activityEvents.forEach((event) => window.removeEventListener(event, resetInactivityTimer));
    };
  }, [user, logout]);

  return (
    <AdminAuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        hasRole,
        login,
        logout,
      }}
    >
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error("useAdminAuth must be used within AdminAuthProvider");
  }
  return context;
}
